import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { IntegrationStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/components/types";
// fallow-ignore-next-line boundary-violation -- shared sales helper lives in workspace zone; move pending
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import { createSelector } from "reselect";
import { selectManagementReducer } from "./managementSelectors";

const selectDynamicsReducer = createStatePropertySelector(selectManagementReducer("dynamicsIntegration"));

const selectDynamicsStatusReducer = createStatePropertySelector(selectDynamicsReducer("crmConnectionStatus"));
const selectDynamicsCrmStateInfo = selectDynamicsReducer("crmStateInfo");

export const selectDynamicsAccountsCount = createSelector(
    selectDynamicsCrmStateInfo,
    (status) => status.accountExportFieldsCount,
);

export const selectDynamicsContactsCount = createSelector(
    selectDynamicsCrmStateInfo,
    (status) => status.contactExportFieldsCount,
);

const selectDynamicsCrmExportLimit = selectDynamicsReducer("crmExportLimit");

export const selectDynamicsCrmExportContactsLimit = createSelector(
    selectDynamicsCrmExportLimit,
    (crmExportLimit) => crmExportLimit?.contactsLimit ?? Infinity,
);

export const selectDynamicsCrmExportLeadsLimit = createSelector(
    selectDynamicsCrmExportLimit,
    (crmExportLimit) => crmExportLimit?.leadsLimit ?? Infinity,
);

const selectDynamicsStatusObject = selectDynamicsReducer("crmConnectionStatus");
const selectIsCurrentUserVerifiedByDynamicsCrm = selectDynamicsReducer("isCurrentUserVerifiedByCrm");

export const selectDynamicsAccountsToggleState = createSelector(
    selectDynamicsStatusObject,
    (crmInfo) => crmInfo.isExportEnabled,
);

export const selectDynamicsContactsToggleState = createSelector(
    selectDynamicsStatusObject,
    (crmInfo) => crmInfo.isContactExportEnabled,
);

const selectExcludeStatusFields = selectDynamicsReducer("excludeStatusFields");
export const selectDynamicsExcludeStatusFields = createSelector(selectExcludeStatusFields, (excludeFields) =>
    excludeFields ? excludeFields : [],
);

export const selectDynamicsExcludeAccountFields = selectDynamicsReducer("excludeAccountFields");

export const selectDynamicsIsLoadingExcludeAccountFields = selectDynamicsReducer("isLoadingExcludeAccountFields");

export const selectDynamicsStatus = selectDynamicsStatusReducer("syncStatus");
export const selectDynamicsLastSynced = selectDynamicsStatusReducer("lastSynced");
export const selectIsDynamicsLeadsFilterEnabled = selectDynamicsStatusReducer("isLeadsFilterEnabled");
export const selectIsDynamicsExportEnabled = selectDynamicsStatusReducer("isExportEnabled");

export const selectIsDynamicsCrmListEnabled = selectDynamicsStatusReducer("isCrmListEnabled");
export const selectDynamicsListObjectsType = selectDynamicsStatusReducer("crmListObjectsType");
export const selectIsDynamicsCrmLabelEnabled = selectDynamicsStatusReducer("isCrmLabelsEnabled");

export const selectDynamicsLoadingState = selectDynamicsReducer("isLoading");
export const selectDynamicsIsLoadingExcludeStatusFields = selectDynamicsReducer("isLoadingExcludeStatusFields");

const selectDynamicsMappingFields = selectDynamicsReducer("mappingFields");
export const selectIsUpdatingDynamicsFilterFields = selectDynamicsReducer("isUpdatingFilterFields");
export const selectIsUpdatingDynamicsStatusField = selectDynamicsReducer("isUpdatingStatusField");

export const selectDynamicsIsNotIntegrated = createSelector(
    selectDynamicsStatus,
    (status) => status === INTEGRATION_STATUS.NotIntegrated,
);

export const selectDynamicsCurrentStatusField = createSelector(selectDynamicsMappingFields, (mappingFields) => {
    if (mappingFields) {
        const filteredFields = mappingFields.filter((field) => field.metadata === "Type");
        return filteredFields.length ? filteredFields[0].externalField : "";
    }
});

export const selectDynamicsFilterFieldNames = createSelector(selectDynamicsMappingFields, (mappingFields) => {
    if (!mappingFields) {
        return [];
    }
    return mappingFields.filter((field) => field.metadata === "AccountListType").map((field) => field.externalField);
});

export const selectDynamicsIntegrationLeadFilterStatus = selectDynamicsReducer("isExclusionOn");

export const selectIsDynamicsCompanyAsLeadExportClaimEnabled = selectDynamicsReducer("companyAsLeadExportClaimEnabled");

export const selectIsDynamicsPredefinedOnCreateUpdateClaimEnabled = selectDynamicsReducer(
    "predefinedOnCreateUpdateClaimEnabled",
);

export const selectDynamicsLeadsToggleState = createSelector(
    selectDynamicsStatusObject,
    (crmInfo) => crmInfo.isLeadsExportEnabled,
);

export const selectDynamicsCompanyAsLeadsToggleState = createSelector(
    selectDynamicsStatusObject,
    (crmInfo) => crmInfo.isCompanyAsLeadExportEnabled,
);

export const selectDynamicsLeadsCount = createSelector(
    selectDynamicsCrmStateInfo,
    (status) => status.leadExportFieldsCount,
);

export const selectDynamicsStatusResponse = createSelector(
    selectDynamicsStatusObject,
    selectDynamicsCrmStateInfo,
    selectDynamicsCrmExportLimit,
    selectIsCurrentUserVerifiedByDynamicsCrm,
    selectIsDynamicsCompanyAsLeadExportClaimEnabled,
    selectIsDynamicsPredefinedOnCreateUpdateClaimEnabled,
    (
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit,
        isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled,
    ): IntegrationStatusResponse => ({
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit: crmExportLimit ?? undefined,
        isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled,
    }),
);
